import moment from "moment";
import { type ReactNode } from "react";

import { IconCheckCircle, IconWarning } from "@btnet/blueprint-icons";
import { PaymentStatusTypes } from "@btnet/services-btnet/handlers/models/paymentStatusTypes";
import type { ProcessorPaymentMethodInfo } from "@btnet/services-btnet/handlers/models/processorPaymentMethodInfo";

import { type OutboundWalletStatus } from "types/enum";

import {
    AddressEntity,
    type IHasAddress,
} from "commonComponents/entity/address/Address/Address.api.types";
import { type PaymentMethod } from "commonComponents/financial/Checkout/Checkout.api.types";

import { type MailCheckTrackingInfo } from "entity/onlinePayment/OutgoingPaymentMailCheck/OutgoingPaymentMailCheck.api.types";
import { mapProcessorPaymentMethodInfoToPaymentMethod } from "entity/rebate/RebateSettings/RebateSettings.utilities";

import type { default as OutboundPaymentsAPIResponse } from "./OutboundPaymentsExternal.api.json";
import type { default as ReceiveCheckResponseAPIResponse } from "./ReceiveCheckResponse.api.json";

/** Mirrors the backend Global.Core.Enums.OutboundPaymentChannel (int-serialized). */
export enum OutboundPaymentChannel {
    Email = 0,
    Sms = 1,
}

export enum PendingOutboundPaymentStatus {
    PendingLienWaivers = 0,
    PendingDepositSelection = 1,
    PaymentAcceptedMail = 2,
    PaymentAcceptedPrint = 3,
    PaymentAcceptedWallet = 4,
    Expired = 5,
    Voided = 6,
    Removed = 7,
    PaymentAcceptedACH = 8,
    PaymentAcceptedUnknown = 9,
}

export class OutboundPaymentsEntity {
    constructor(data: typeof OutboundPaymentsAPIResponse) {
        this.encodedBuilderId = data.encodedBuilderId;
        this.encodedPaymentId = data.encodedPaymentId;
        this.encodedSubId = data.encodedSubId;
        this.status = data.status;
        this.builderName = data.builderName;
        this.paymentAmount = data.paymentAmount;
        this.expirationDate = new Date(data.expirationDate);
        this.walletStatus = data.walletStatus;
        this.subHasActiveWallet = data.subHasActiveWallet;
        this.globalUserId = data.globalUserId;
        this.channel = data.channel ?? OutboundPaymentChannel.Email; // Default to Email if channel is not provided (for backward compatibility)
        this.lastFour = data.lastFour ?? undefined;
        this.hasOptedIn = data.hasOptedIn ?? false;
    }

    encodedBuilderId: string;
    encodedPaymentId: string;
    encodedSubId: string;
    status: PendingOutboundPaymentStatus;
    builderName: string;
    paymentAmount: number;
    expirationDate: Date;
    walletStatus: OutboundWalletStatus;
    subHasActiveWallet: boolean;
    globalUserId: number;
    channel: OutboundPaymentChannel;
    /** Populated only for SMS-channel links. */
    lastFour?: string;
    hasOptedIn: boolean;
}

export class ReceiveCheckResponse implements IHasAddress {
    constructor(data: typeof ReceiveCheckResponseAPIResponse) {
        this.encodedBuilderId = data.encodedBuilderId;
        this.encodedPaymentId = data.encodedPaymentId;
        this.signatureUrl = data.signatureUrl;
        this.logoUrl = data.logoUrl;
        this.payeeName = data.payeeName;
        this.payerName = data.payerName;
        this.memo = data.memo;
        this.checkNumber = data.checkNumber;
        this.recipientLastFour = data.recipientLastFour;
        this.paymentStatus = data.paymentStatus as PaymentStatusTypes;
        this.paymentExpirationDays = data.paymentExpirationDays;
        this.paymentAmount = data.paymentAmount;
        this.payerAddress = data.payerAddress && new AddressEntity(data.payerAddress);
        this.address = data.payeeAddress && new AddressEntity(data.payeeAddress);
        this.paymentDate = data.paymentDate ? moment(data.paymentDate) : undefined;
        this.hadLienWaivers = data.hadLienWaivers;
        this.trackingInfo = data.trackingInfo ?? undefined;
        this.savedPaymentMethod = data.savedPaymentMethod
            ? mapProcessorPaymentMethodInfoToPaymentMethod(
                  data.savedPaymentMethod as ProcessorPaymentMethodInfo,
              )
            : null;
        this.isInReview = data.isInReview ?? false;
    }

    encodedBuilderId: string;
    encodedPaymentId: string;
    signatureUrl: string;
    logoUrl: string;
    payeeName: string;
    payerName: string;
    memo: string;
    checkNumber: string;
    recipientLastFour: string;
    paymentStatus?: PaymentStatusTypes;
    paymentExpirationDays: number;
    paymentAmount: number;
    payerAddress: AddressEntity;
    address: AddressEntity;
    paymentDate: moment.Moment | undefined;
    hadLienWaivers: boolean;
    trackingInfo: MailCheckTrackingInfo[] | undefined;
    savedPaymentMethod: PaymentMethod | null;
    isInReview: boolean;
}

export function getOutboundConfirmationHeaderInfo(
    paymentStatus: PaymentStatusTypes | undefined,
    successHeaderText: string,
    successSubHeaderText: string = "",
) {
    let icon: ReactNode;
    let headerText;
    let iconWrapperClassName;
    let subText;

    if (paymentStatus === PaymentStatusTypes.Failed) {
        icon = <IconWarning className="WarningIcon" size="2xl" />;
        headerText = "Payment couldn't be processed";
        subText = "Contact your builder for more information";
        iconWrapperClassName = "WarningIconWrapper";
    } else {
        icon = <IconCheckCircle className="CheckIcon" size="2xl" />;
        headerText = successHeaderText;
        subText = successSubHeaderText;
        iconWrapperClassName = "CheckIconWrapper";
    }

    return {
        icon,
        headerText,
        subText,
        iconWrapperClassName,
    };
}
